<template>
    <div>
        <q-dialog :model-value="show" @hide="onCancel">
            <q-card class="q-pa-md" style="width: 90%">
                <q-card-section class="q-pt-none">
                    <q-form @submit="onSplit()" ref="splitForm">
                        <div class="text-h6 q-mb-md">
                            {{ $t('Split the event') }}
                        </div>
                        <p>
                            <b class="q-mr-xs">{{ $t('Start') }}:</b>
                            {{ formatDate(start, userLocale) }}
                        </p>

                        <p>{{ $t('Select when to split the event') }}:</p>
                        <date-time-picker
                            id="split-event-input"
                            v-model="dateTimePicker.value"
                            :label="$t('Splitting time')"
                            :dateRange="{
                                start,
                                end
                            }"
                            :rules="[val => validateSplitTime(val)]"
                            :readonly="true"
                        />
                        <div
                            class="form__actions"
                            :class="{ 'q-mt-lg': $q.screen.lt.md }"
                        >
                            <q-btn
                                id="submit-split-button"
                                :label="$t('Confirm')"
                                type="submit"
                                class="float-right"
                                color="primary"
                                text-color="white"
                                :loading="isLoading"
                            />
                            <q-btn
                                @click="onCancel"
                                :label="$t('Cancel')"
                                class="float-right q-mr-sm"
                                color="white"
                                text-color="primary"
                                v-close-popup
                                outline
                                :disable="isLoading"
                            />
                        </div>
                    </q-form>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import { date } from 'quasar';
import AsyncManager from '@utils/asyncManager.js';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'SplitEventDialog',
    components: { DateTimePicker },
    emits: ['splitSuccess', 'close'],
    props: {
        event: {
            type: Object,
            required: true
        },
        show: {
            type: Boolean,
            required: true
        }
    },
    created() {
        // initialize async manager instance
        this.asyncManager = new AsyncManager({});
        // initialize datetime to be within the valid date range
        const startDate = new Date(this.start);
        this.dateTimePicker.value = date
            .addToDate(startDate, { seconds: 1 })
            .toISOString();
    },
    beforeUnmount() {
        // let the celery task continue executing but stop periodic status check
        try {
            this.asyncManager.stopPeriodicStatusCheck();
        } catch (error) {
            console.error(error.message);
        }
    },
    data() {
        return {
            dateTimePicker: {
                value: new Date().toISOString()
            },
            isLoading: false
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the id of the selected event to be split.
         * @return {Number} - event id
         */
        eventId() {
            return this.event.id;
        },
        /**
         * Get the start time of the selected event.
         * @return {String} - start time
         */
        start() {
            return this.event.start;
        },
        /**
         * Get the end time of the selected event.
         * @return {String} - end time
         */
        end() {
            return this.event.end;
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        formatDate(datetime, locale) {
            return getLocaleFormat(datetime, locale);
        },
        /**
         * Split the selected downtime event.
         * Triggered when the user clicks Split button.
         */
        async onSplit() {
            // disable the submit button
            this.isLoading = true;
            // prepare the request record
            const url = `events/${this.eventId}/split/`;
            const payload = {
                date: this.dateTimePicker.value,
                asynchronous: true
            };
            // split the event
            try {
                const result = await this.asyncManager.asyncLoad(url, payload);
                if ('error' in result[0]) {
                    // error during split execution
                    const error = result[0]['error'];
                    if (error === 'unprocessed_phases') {
                        this.$notifyError(
                            this.$t(
                                'Data for this event is still processing. Please try again later.'
                            )
                        );
                    } else if (error === 'latest_phase') {
                        this.$notifyError(
                            this.$t(
                                'Failed to split event. This event is no longer the latest phase.'
                            )
                        );
                    } else if (error === 'split_date') {
                        this.$notifyError(this.$t('Split date is invalid.'));
                    } else if (error == 'uneditable_phase') {
                        this.$notifyError(
                            this.$t(
                                "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                {
                                    editablePeriod: parseInt(
                                        process.env
                                            .EDITABLE_PHASE_DURATION_DAYS || 5 // 5 days
                                    )
                                }
                            )
                        );
                    } else {
                        this.$notifyError(
                            this.$t(
                                'Failed to split phase. Please try again later.'
                            ),
                            error
                        );
                    }
                } else {
                    // split was succesful
                    this.$notifySuccess(
                        this.$t('Downtime event has been split.')
                    );
                    this.$emit('splitSuccess', null);
                }
            } catch (error) {
                if (
                    error &&
                    typeof error == 'object' &&
                    'uneditable_phase' in error
                ) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to split event. Please try again later.'
                        ),
                        error
                    );
                }
            }

            this.isLoading = false;
        },
        /**
         * Triggered when user clicks Cancel button.
         */
        onCancel() {
            this.$emit('close', null);
        },
        /**
         * Validate the split time input.
         * @param {String} val - User input from datetime picker.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateSplitTime(val) {
            // false is an error
            if (val.length > 0) {
                const startTimetamp = Date.parse(this.start);
                const endTimetamp = Date.parse(this.end);

                const splitTimestamp = Date.parse(this.dateTimePicker.value);

                if (
                    splitTimestamp <= startTimetamp ||
                    splitTimestamp >= endTimetamp
                ) {
                    return this.$t(
                        'Splitting time should be between current start and end'
                    );
                }
            } else {
                return this.$t('Pick a date and time');
            }
            return true;
        }
    }
};
</script>

<style lang="scss" scoped></style>
